import { createContext, useContext, useEffect, useState, useRef, useCallback } from "react";
import { v4 as uuidv4 } from "uuid";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { useAuth } from "../hooks/useAuth";
import {
  useStartVisitSessionMutation,
  useUpdateVisitSessionMutation,
  useEndVisitSessionMutation,
  useTrackPageVisitMutation,
} from "../graphql/generated/graphql";
import Sentry from "../utils/sentry";

const usePathname = () => {
  try {
    const { useLocation } = require("react-router-dom");
    return useLocation().pathname;
  } catch {
    return typeof window !== "undefined" ? window.location.pathname : "/";
  }
};

interface VisitSessionContextType {
  isTracking: boolean;
  sessionId: string | null;
  endSession: () => Promise<void>;
}

const VisitSessionContext = createContext<VisitSessionContextType>({
  isTracking: false,
  sessionId: null,
  endSession: async () => {},
});

export const useVisitSession = () => useContext(VisitSessionContext);

export const VisitSessionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const currentPath = usePathname();

  const [previousPath, setPreviousPath] = useState(currentPath);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isTracking, setIsTracking] = useState(false);
  const [initialPageVisitTracked, setInitialPageVisitTracked] = useState(false);

  const heartbeatRef = useRef<NodeJS.Timeout | null>(null);
  const hasEndedRef = useRef(false);
  const blurTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const debounceDuration = 10000; // 10 seconds debounce for blur/focus events

  const [startVisitSession] = useStartVisitSessionMutation();
  const [updateVisitSession] = useUpdateVisitSessionMutation();
  const [endVisitSession] = useEndVisitSessionMutation();
  const [trackPageVisit] = useTrackPageVisitMutation();

  /** Full Apollo end + local cleanup */
  const clearSession = useCallback(async () => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
    if (!sessionId) return;

    try {
      await endVisitSession({ variables: { sessionId } });
    } catch (err) {
      Sentry.captureException(err);
      console.error("Error ending visit session:", err);
    }

    window.sessionStorage?.removeItem("visitSessionId");
    await AsyncStorage.removeItem("visitSessionId");

    setSessionId(null);
    setIsTracking(false);
    setInitialPageVisitTracked(false);
  }, [sessionId, endVisitSession]);

  /** Create a new session (or replace stale), reset end-guard, start heartbeat + initial pageVisit */
  const initSession = useCallback(async () => {
    hasEndedRef.current = false;

    try {
      // Tear down any stale session
      const existing =
        window.sessionStorage?.getItem("visitSessionId") ?? (await AsyncStorage.getItem("visitSessionId"));
      if (existing) {
        await endVisitSession({ variables: { sessionId: existing } }).catch(() => {});
        window.sessionStorage?.removeItem("visitSessionId");
        await AsyncStorage.removeItem("visitSessionId");
      }

      if (isAuthenticated) {
        const result = await startVisitSession();
        const newId = result.data?.startVisitSession.id;

        if (!newId) {
          throw new Error("Failed to get session ID from server");
        }

        setSessionId(newId);

        if (window.sessionStorage) {
          window.sessionStorage.setItem("visitSessionId", newId);
        } else {
          await AsyncStorage.setItem("visitSessionId", newId);
        }

        setIsTracking(true);

        heartbeatRef.current = setInterval(() => {
          updateVisitSession({ variables: { sessionId: newId } }).catch((err) => {
            console.error("Error updating session:", err);
          });
        }, 30_000);

        // initial page visit
        const full = window.location.pathname + window.location.search;
        await trackPageVisit({ variables: { sessionId: newId, path: full } }).catch((err) => {
          console.error("Error tracking initial page visit:", err);
        });

        // Mark that we've tracked the initial page visit
        setInitialPageVisitTracked(true);
        setPreviousPath(full);
      }
    } catch (err) {
      Sentry.captureException(err);
      console.error("Error initializing session:", err);
    }
  }, [isAuthenticated, startVisitSession, updateVisitSession, endVisitSession, trackPageVisit]);

  /** One‐shot keepalive fetch to end session on hidden/unload */
  const sendEndRequest = useCallback(() => {
    if (hasEndedRef.current || !sessionId) return;
    hasEndedRef.current = true;

    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }

    // Store the sessionId in a local variable before clearing state
    const currentSessionId = sessionId;

    // Clear state first before API call to prevent duplicate end requests
    setSessionId(null);
    setIsTracking(false);
    setInitialPageVisitTracked(false);
    window.sessionStorage?.removeItem("visitSessionId");

    const headers: Record<string, string> = { "Content-Type": "application/json" };
    const token = localStorage.getItem("accessToken");
    if (token) headers["Authorization"] = `Bearer ${token}`;

    // keepalive: true for unload
    fetch("/api/visit-session/end-session", {
      method: "POST",
      headers,
      body: JSON.stringify({ sessionId: currentSessionId }),
      keepalive: true,
    }).catch((err) => console.warn("Keepalive end-session failed:", err));
  }, [sessionId]);

  // 1) On mount → initSession
  useEffect(() => {
    initSession();
    return () => {
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
        blurTimeoutRef.current = null;
      }
    };
  }, [initSession]);

  // 2) Track route changes
  useEffect(() => {
    const track = async () => {
      // Skip tracking if session isn't established yet, or if we're not tracking,
      // or if this is the initial page load (already tracked in initSession)
      if (!sessionId || !isTracking || !initialPageVisitTracked) return;

      const full = window.location.pathname + window.location.search;
      if (full !== previousPath) {
        try {
          await trackPageVisit({ variables: { sessionId, path: full } });
          setPreviousPath(full);
        } catch (err) {
          console.error("Error tracking page visit:", err);
        }
      }
    };
    track();
  }, [
    currentPath,
    window?.location?.search,
    previousPath,
    sessionId,
    isTracking,
    trackPageVisit,
    initialPageVisitTracked,
  ]);

  // 3) Explicit logout
  useEffect(() => {
    if (!isAuthenticated && sessionId) {
      clearSession();
    }
  }, [isAuthenticated, sessionId, clearSession]);

  // 4) Hidden/unload & visibility change + window focus events
  useEffect(() => {
    const onVisibility = () => {
      if (!isAuthenticated) return;
      if (document.visibilityState === "hidden") {
        sendEndRequest();
      } else if (document.visibilityState === "visible") {
        // Only start a new session if we don't already have one
        if (!hasEndedRef.current) {
          initSession();
        }
      }
    };

    const onBlur = () => {
      if (!isAuthenticated) return;

      // Clear any existing blur timeout
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }

      blurTimeoutRef.current = setTimeout(() => {
        sendEndRequest();
        blurTimeoutRef.current = null;
      }, debounceDuration);
    };

    const onFocus = () => {
      if (!isAuthenticated) return;

      // If we have a pending blur timeout, cancel it
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
        blurTimeoutRef.current = null;
        return; // Don't start a new session if we're just canceling a pending end
      }

      // Only start a new session if we don't already have one
      if (!sessionId && hasEndedRef.current) {
        initSession();
      }
    };

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", sendEndRequest);
    window.addEventListener("blur", onBlur);
    window.addEventListener("focus", onFocus);

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", sendEndRequest);
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("focus", onFocus);
    };
  }, [isAuthenticated, sendEndRequest, initSession, sessionId]);

  return (
    <VisitSessionContext.Provider value={{ isTracking, sessionId, endSession: clearSession }}>
      {children}
    </VisitSessionContext.Provider>
  );
};
